import React, { useMemo } from 'react';
import {
  FunnelListContainer,
  FunnelItemContainer,
  LeftSection,
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbArrow,
  RightSection,
  MetricColumn,
  MetricValue,
  MetricLabel,
  ActionButton,
  NAText,
  SplitLine,
} from './styled';
import { usePostPatternsQuery } from '@src/services/query/applang/queryHooks';
import { useDatasetStore } from '@shared/store';
import { useAggregations, usePatternsQuery } from '@src/services/queries';
import type { Pattern } from '@src/shared/types/patterns';
import type { PatternsQueryRequest } from '@src/services/query/applang/queries';
import { formatMetricValue } from '@src/shared/utils/number';
import { Spin, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { readableTime } from '@src/pages/journey-map/utils/formatTime';
import FunnelIcon from '@src/icons/funnel.svg';
import { useNavigate } from 'react-router-dom';
import type { FilterCondition } from '@src/shared/types/user-journeys';

interface FunnelData {
  tag: string;
  p_id: string;
  steps: string[];
  unique_user_id?: number;
  count_of_records?: number;
  conversion_rate?: number;
  unique_client_id?: number;
  mean_time_since_beginning_ms?: number;
}
const patternDefinitions = [
  {
    id: 'unique_user_id',
    description:
      'Count of unique users who followed the complete pattern from Step 1 through the last step without skipping any intermediate steps.',
    unit: '#',
  },
  {
    id: 'count_of_records',
    description:
      'Total number of times the complete pattern, from Step 1 through the last step, was completed. This counts all instances, including repeat completions by the same user.',
    unit: '#',
  },
  {
    id: 'mean_time_since_prior_ms',
    description:
      'Average time taken to complete the entire pattern from Step 1 through the last step.',
    unit: 'ms',
  },
  {
    id: 'mean_time_since_beginning_ms',
    description:
      'Average time taken to complete the entire pattern from Step 1 through the last step.',
    unit: 'ms',
  },
  {
    id: 'conversion_rate',
    description:
      'Percentage of funnel initiations that successfully completed the entire pattern from Step 1 through the last step. Calculated as (Total Conversions ÷ Total Initiations on Step 1) × 100%.',
    unit: '%',
  },
  {
    id: 'unique_client_id',
    description:
      'Count of unique Devices (ClientIDs) that followed the complete pattern from Step 1 through the last step without skipping any intermediate steps.',
    unit: '#',
  },
];

const FunnelList = ({
  isLoadingPatterns,
  interval,
  globalFilter,
}: {
  isLoadingPatterns: boolean;
  interval: [string, string];
  globalFilter: FilterCondition[][];
}) => {
  const { patterns: patternsData } = useDatasetStore();
  const aggregationsQuery = useAggregations();
  const aggregations = aggregationsQuery.data?.aggregations;
  const navigate = useNavigate();

  // Filter pattern-type aggregations
  const patternAggregations = useMemo(() => {
    if (!aggregations) return [];
    return aggregations
      .filter((agg) => agg.type === 'pattern')
      .map((agg) => {
        const definition = patternDefinitions.find((m) => m.id === agg.id);
        return {
          id: agg.id,
          name: agg.name,
          description: definition?.description,
          unit: definition?.unit,
        };
      });
  }, [aggregations]);

  // Fetch metrics for all patterns in one query
  const { data: patternsMetricsData, isLoading: isLoadingMetrics } = usePatternsQuery({
    patterns: patternsData.map((pattern: Pattern) => pattern.p_id),
    time_range: interval,
    timeout_ms: 24 * 60 * 60 * 1000,
    filters: globalFilter,
    aggregations: patternAggregations.map((metric) => metric.id),
  });

  // Transform API response - get metrics from first step for each pattern
  const patternMetrics = useMemo(() => {
    const metricsMap: Record<string, any> = {};
    if (patternsMetricsData && typeof patternsMetricsData === 'object') {
      // Response format: { "p_281": [{step data}], "p_263": [{step data}] }
      Object.entries(patternsMetricsData).forEach(([patternId, steps]) => {
        const correctStepLength = patternsData.find(
          (pattern: Pattern) => pattern.p_id === patternId,
        )?.critical_events.length;
        if (correctStepLength && steps.length !== correctStepLength) {
          return;
        }
        if (Array.isArray(steps) && steps.length > 0) {
          metricsMap[patternId] = steps[steps.length - 1];
        }
      });
    }
    return metricsMap;
  }, [patternsMetricsData, patternsData]);

  // Transform pattern data to FunnelData format
  const transformedData: FunnelData[] = useMemo(() => {
    if (!patternsData || !Array.isArray(patternsData)) return [];
    if (isLoadingMetrics) {
      return patternsData.map((pattern: Pattern) => ({
        tag: pattern.name || `Pattern ${pattern.p_id}`,
        p_id: pattern.p_id,
        steps: pattern.critical_events.map((event) => event.name),
      }));
    }
    return patternsData
      .map((pattern: Pattern) => {
        // Use pattern ID directly to lookup metrics
        const metrics = patternMetrics[pattern.p_id] || {};

        return {
          tag: pattern.name || `Pattern ${pattern.p_id}`,
          p_id: pattern.p_id,
          steps: pattern.critical_events.map((event) => event.name),
          ...metrics,
        } as FunnelData;
      })
      .filter((item) => item.unique_user_id && item.unique_user_id > 0) // Filter out items with 0 or undefined unique_user_id
      .sort((a, b) => (b.unique_user_id || 0) - (a.unique_user_id || 0)); // Sort by unique_user_id descending
  }, [patternsData, patternMetrics, isLoadingMetrics]);

  const goToFunnelDetaila = (p_id: string) => {
    const item = patternsData.find((pattern: Pattern) => pattern.p_id === p_id);

    if (!item) return;

    void navigate(`/funnels/temp`, {
      state: {
        critical_events: item.critical_events.map((it) => it.ce_id),
        filters: globalFilter,
        time_range: interval,
        timeout: {
          val: 24,
          unit: 'h',
        },
        propagation_config: Object.fromEntries(
          globalFilter.flat().map(({ field }) => [field, 'context']),
        ),
      },
    });
  };

  const FunnelItem = ({ item }: { item: FunnelData }) => {
    const usersAggregation = patternAggregations.find(
      (agg) => agg.id === 'unique_user_id',
    ) as Aggregation;
    const conversionsNumAggregation = patternAggregations.find(
      (agg) => agg.id === 'count_of_records',
    ) as Aggregation;
    const conversionsPercentageAggregation = patternAggregations.find(
      (agg) => agg.id === 'conversion_rate',
    ) as Aggregation;
    const devicesNumAggregation = patternAggregations.find(
      (agg) => agg.id === 'unique_client_id',
    ) as Aggregation;
    const durationAggregation = patternAggregations.find(
      (agg) => agg.id === 'mean_time_since_beginning_ms',
    ) as Aggregation;

    const MetricColumnRenderer = ({ metric }: { metric: Aggregation }) => {
      if (!metric) return null;
      return (
        <MetricColumn>
          <Spin spinning={isLoadingMetrics} size="small">
            <MetricValue>
              {!isLoadingMetrics && (formatMetricValue(metric, item) || <NAText>NA</NAText>)}
            </MetricValue>
          </Spin>
          <MetricLabel>
            {metric.name}
            <Tooltip title={`${metric.description}`}>
              <InfoCircleOutlined style={{ marginLeft: '4px', color: '#999', fontSize: '14px' }} />
            </Tooltip>
          </MetricLabel>
        </MetricColumn>
      );
    };
    return (
      <FunnelItemContainer>
        <LeftSection>
          {/* <TagBadge>{item.tag}</TagBadge> */}
          <BreadcrumbContainer>
            {item.steps.map((step, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>{step}</BreadcrumbItem>
                {index < item.steps.length - 1 && <BreadcrumbArrow>→</BreadcrumbArrow>}
              </React.Fragment>
            ))}
          </BreadcrumbContainer>
        </LeftSection>

        <RightSection>
          <SplitLine />
          <MetricColumnRenderer metric={usersAggregation} />
          <MetricColumnRenderer metric={conversionsNumAggregation} />

          <MetricColumn>
            <Spin spinning={isLoadingMetrics} size="small">
              <MetricValue>
                {!isLoadingMetrics &&
                  (item.mean_time_since_beginning_ms ? (
                    readableTime(item.mean_time_since_beginning_ms)
                  ) : (
                    <NAText>NA</NAText>
                  ))}
              </MetricValue>
            </Spin>
            <MetricLabel>
              Duration{' '}
              <Tooltip title={`${durationAggregation.description}`}>
                <InfoCircleOutlined
                  style={{ marginLeft: '4px', color: '#999', fontSize: '14px' }}
                />
              </Tooltip>
            </MetricLabel>
          </MetricColumn>
          <MetricColumnRenderer metric={conversionsPercentageAggregation} />
          <MetricColumnRenderer metric={devicesNumAggregation} />

          <ActionButton
            onClick={() => {
              goToFunnelDetaila(item.p_id);
            }}
          >
            <FunnelIcon />
          </ActionButton>
        </RightSection>
      </FunnelItemContainer>
    );
  };
  return (
    <FunnelListContainer>
      <Spin spinning={isLoadingPatterns} style={{ minHeight: '300px' }}>
        {transformedData.map((item, index) => (
          <FunnelItem key={index} item={item} />
        ))}
      </Spin>
    </FunnelListContainer>
  );
};

export default FunnelList;
